import { Component, ComponentInterface, Element, Fragment, h, Host, JSX, Listen, Prop, State } from '@stencil/core';
import { getDataOnFromTheme, WorkbenchTheme } from '../../shared/themes';
import { isSlotted } from '../../utils/is-slotted';
import { bindAvatar } from './avatar/avatar';
import { Icon } from './icon/icon';
import { bindLabel } from './label/label';
import { bindLead } from './lead/lead';

/**
 * @slot image - optional image element
 * @slot icon - optional icon element
 * @slot label - optional label element
 */
@Component({
  tag: 'wb-navigation-item',
  styleUrl: 'navigation-item.scss',
  shadow: true,
})
export class NavigationItemComponent implements ComponentInterface {
  @Element()
  public el: HTMLElement;

  /**
   * The colour scheme
   * @deprecated Use `data-on` attribute instead
   * */
  @Prop({ reflect: true })
  public theme?: WorkbenchTheme;

  /** Renders an anchor tag linked to the passed in URL (fragment) */
  @Prop()
  public href: string | undefined;

  /** Can be used in combination with `href` property */
  @Prop()
  public rel: string | undefined;

  /** Can be used in combination with `href` property */
  @Prop()
  public target: string | undefined;

  /** the button's name attribute */
  @Prop()
  public name?: string;

  /** the aria-label attribute */
  @Prop()
  public navigationAriaLabel?: string;

  /** Profile's full name */
  @Prop({ reflect: true })
  public fullName?: string = '';

  /** The notification state */
  @Prop({ reflect: true })
  public hasNotification: boolean = false;

  /** Force notification on label */
  @Prop({ reflect: true })
  public forceLabelNotification: boolean = false;

  /** The avatar state */
  @Prop({ reflect: true })
  public hasAvatar: boolean = false;

  /** The dropdown state */
  @Prop({ reflect: true })
  public hasDropdown: boolean = false;

  /** Restrict the max length of the label */
  @Prop()
  public ellipsis: boolean = true;

  @State()
  public hasImage: boolean;

  @State()
  public hasIcon: boolean;

  @State()
  public hasAvatarIcon: boolean;

  @State()
  public hasLabel: boolean;

  @State()
  public hasLead: boolean;

  @Listen('focus')
  @Listen('blur')
  @Listen('mouseenter')
  @Listen('mouseleave')
  public onAvatarEvent(event: MouseEvent | FocusEvent): void {
    const newEvent = new Event(event.type);
    this.avatarEl?.dispatchEvent(newEvent);
  }

  protected avatarEl!: HTMLElement;

  public connectedCallback(): void {
    this.checkSlots();
  }

  public componentWillUpdate(): void {
    this.checkSlots();
  }

  private checkSlots = (): void => {
    this.hasImage = isSlotted(this.el, 'image');
    this.hasIcon = isSlotted(this.el, 'icon') && !this.hasAvatar;
    this.hasAvatarIcon = isSlotted(this.el, 'icon');
    this.hasLabel = isSlotted(this.el, 'label');
    this.hasLead = isSlotted(this.el, 'lead');
  };

  private bindAvatarEl = (el: HTMLElement): void => {
    this.avatarEl = el;
  };

  private handleImageSlotChange = (event: Event) => {
    const slot = event.target as HTMLSlotElement;
    this.hasImage = slot.assignedElements().length > 0;
  };

  private handleAvatarIconSlotChange = (event: Event) => {
    const slot = event.target as HTMLSlotElement;
    this.hasAvatarIcon = slot.assignedElements().length > 0;
  };

  private handleIconSlotChange = (event: Event) => {
    const slot = event.target as HTMLSlotElement;
    this.hasIcon = slot.assignedElements().length > 0 && !this.hasAvatar;
  };

  private handleLabelSlotChange = (event: Event) => {
    const slot = event.target as HTMLSlotElement;
    this.hasLabel = slot.assignedElements().length > 0;
  };

  private handleLeadSlotChange = (event: Event) => {
    const slot = event.target as HTMLSlotElement;
    this.hasLead = slot.assignedElements().length > 0;
  };

  public render(): JSX.Element {
    const Avatar = bindAvatar(this);
    const Label = bindLabel(this);
    const Lead = bindLead(this);

    const Elements = (): JSX.Element => {
      return (
        <Fragment>
          {this.hasAvatar && (
            <Avatar //
              fullName={this.fullName}
              hasAvatar={this.hasAvatar}
              hasImage={this.hasImage}
              hasAvatarIcon={this.hasAvatarIcon}
              hasLabel={this.hasLabel}
              hasNotification={this.hasNotification}
              forceLabelNotification={this.forceLabelNotification}
              handleImageSlotChange={this.handleImageSlotChange}
              handleAvatarIconSlotChange={this.handleAvatarIconSlotChange}
              bindAvatarEl={this.bindAvatarEl}
            />
          )}
          {this.hasIcon && (
            <Icon //
              hasAvatar={this.hasAvatar}
              hasLabel={this.hasLabel}
              hasNotification={this.hasNotification}
              forceLabelNotification={this.forceLabelNotification}
              handleIconSlotChange={this.handleIconSlotChange}
            />
          )}
          {(this.hasLabel || this.hasLead) && (
            <span class="labels">
              {this.hasLead && (
                <Lead //
                  ellipsis={this.ellipsis}
                  handleLeadSlotChange={this.handleLeadSlotChange}
                />
              )}
              {this.hasLabel && (
                <Label //
                  hasAvatar={this.hasAvatar}
                  hasIcon={this.hasIcon}
                  hasLabel={this.hasLabel}
                  hasNotification={this.hasNotification}
                  hasDropdown={this.hasDropdown}
                  ellipsis={this.ellipsis}
                  forceLabelNotification={this.forceLabelNotification}
                  handleLabelSlotChange={this.handleLabelSlotChange}
                />
              )}
            </span>
          )}
        </Fragment>
      );
    };

    return (
      <Host class="wb-navigation-item" tabindex="0" data-on={getDataOnFromTheme(this.el, this.theme)}>
        {this.href ? (
          <a class="cta" href={this.href} rel={this.rel} target={this.target} aria-label={this.navigationAriaLabel} tabindex="-1">
            <Elements />
          </a>
        ) : (
          <button class="cta" type="button" name={this.name} aria-label={this.navigationAriaLabel} tabindex="-1">
            <Elements />
          </button>
        )}
      </Host>
    );
  }
}
